import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { ApiError } from '../../lib/api-client';
import { isAuthenticated, login } from '../../lib/auth';
import { useHospitalBrand } from '../../lib/hospital-brand';
import { authenticate } from './auth.api';
import { loginFormSchema, type LoginFormValues } from './login.schema';

const UserIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-6 8-6s8 2 8 6" />
  </svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4.5" y="10.5" width="15" height="10" rx="2.4" /><path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
  </svg>
);

const EyeIcon = ({ open }: { open: boolean }) => open ? (
  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 12s3.8-7 10-7 10 7 10 7-3.8 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" />
  </svg>
) : (
  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 3l18 18M10.6 5.2A10.9 10.9 0 0 1 12 5c6.2 0 10 7 10 7a17.1 17.1 0 0 1-4 4.7M6.5 6.6C4 8.3 2 12 2 12s3.8 7 10 7c1.4 0 2.7-.3 3.8-.8M9.9 9.9a3 3 0 0 0 4.2 4.2" />
  </svg>
);

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3Z" /><path d="M9 12l2 2 4-4" />
  </svg>
);

export const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hospitalBrand = useHospitalBrand();
  const hospitalLogo = hospitalBrand.smallLogoDataUrl ?? hospitalBrand.logoDataUrl;
  const [authError, setAuthError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: { identifier: '', password: '', remember: false },
  });

  if (isAuthenticated()) {
    return <Navigate to="/" replace />;
  }

  const requestedPath = (location.state as { from?: { pathname: string } } | null)?.from?.pathname;
  const submitLogin = handleSubmit(async (values) => {
    setAuthError(null);
    try {
      const session = await authenticate(values);
      login(session, values.remember);
      const destination = requestedPath ?? '/';
      void navigate(destination, { replace: true });
    } catch (error) {
      setAuthError(error instanceof ApiError ? error.message : 'Unable to sign in. Please try again.');
    }
  });

  return (
    <div className="login-scene">
      <div className="login-scene-glow login-scene-glow-a" aria-hidden="true" />
      <div className="login-scene-glow login-scene-glow-b" aria-hidden="true" />
      <div className="login-scene-grid" aria-hidden="true" />
      <div className="login-brand-mark">
        <span className={`login-brand-logo${hospitalLogo ? ' has-logo' : ''}`} aria-hidden="true">
          {hospitalLogo ? <img src={hospitalLogo} alt="" /> : '+'}
        </span>
        <div><strong>{hospitalBrand.title}</strong><small>Hospital workspace</small></div>
      </div>

      <section className="login-card" aria-labelledby="login-title">
        <div className="login-avatar"><UserIcon /></div>
        <h1 id="login-title">User Login</h1>
        <p className="login-subtitle">Staff can sign in with their assigned login ID</p>
        {authError ? <div className="login-alert" role="alert">{authError}</div> : null}

        <form onSubmit={(event) => void submitLogin(event)} noValidate>
          <label className="login-field">
            <span className="login-field-icon"><UserIcon /></span>
            <input {...register('identifier')} type="text" placeholder="Username or email login ID" autoComplete="username" aria-invalid={Boolean(errors.identifier)} />
          </label>
          {errors.identifier ? <small className="login-field-error">{errors.identifier.message}</small> : null}
          <label className="login-field">
            <span className="login-field-icon"><LockIcon /></span>
            <input {...register('password')} type={showPassword ? 'text' : 'password'} placeholder="Password" autoComplete="current-password" aria-invalid={Boolean(errors.password)} />
            <button type="button" className="login-field-toggle" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? 'Hide password' : 'Show password'}>
              <EyeIcon open={showPassword} />
            </button>
          </label>
          {errors.password ? <small className="login-field-error">{errors.password.message}</small> : null}
          <div className="login-row">
            <label className="login-checkbox"><input type="checkbox" {...register('remember')} />Keep me logged in</label>
            <span>Contact administration for access</span>
          </div>
          <div className="login-actions">
            <button className="login-btn login-btn-primary" type="submit" disabled={isSubmitting}>{isSubmitting ? 'Signing in...' : 'Log in'}</button>
            <button className="login-btn login-btn-ghost" type="button" onClick={() => { reset(); setAuthError(null); }}>Clear</button>
          </div>
        </form>
      </section>
      <p className="login-footnote"><ShieldIcon /> Secure Hospital Login</p>
    </div>
  );
};
